import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, Egg, CheckCircle, LogOut, User, BarChart, History, Award } from 'lucide-react';
import { getAuth, signOut } from 'firebase/auth';
import { useState } from 'react';

export default function MainDashboard() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const userName = auth.currentUser?.displayName || auth.currentUser?.email || 'User';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Referral Verified Networking Dashboard</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full">
              <User className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{userName}</span>
            </div>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              disabled={isLoggingOut}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-10 bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {userName.split('@')[0]}</h2>
          <p className="text-gray-600 mb-4">
            Use these tools to supercharge your networking and build better referral relationships.
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <BarChart className="h-4 w-4" />
            <span>Your tools are ready to use. Select one below to get started.</span>
          </div>
        </div>
        
        {/* Tools Grid */}
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Networking Tools</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {/* Pitch Analyzer Card */}
          <Card className="hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] border-indigo-100 hover:border-indigo-300">
            <CardHeader className="pb-2">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
                <Mic className="h-6 w-6 text-indigo-600" />
              </div>
              <CardTitle className="text-indigo-900">Pitch Analyzer</CardTitle>
              <CardDescription>Perfect your elevator pitch with AI</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4 min-h-[60px]">
                Analyze and improve your networking pitch with AI-powered feedback and scoring.
              </p>
              <Button 
                onClick={() => navigate('/dashboard/pitch-analyzer')} 
                className="w-full bg-indigo-600 hover:bg-indigo-700 transition-all"
              >
                <Mic className="h-4 w-4 mr-2" />
                Analyze Your Pitch
              </Button>
            </CardContent>
          </Card>

          {/* Golden Goose Card */}
          <Card className="hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] border-yellow-100 hover:border-yellow-300">
            <CardHeader className="pb-2">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mb-2">
                <Egg className="h-6 w-6 text-yellow-600" />
              </div>
              <CardTitle className="text-yellow-900">Golden Goose</CardTitle>
              <CardDescription>Find your best referral partners</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4 min-h-[60px]">
                Generate powerful referral requests and identify your ideal Golden Goose opportunities.
              </p>
              <Button 
                onClick={() => navigate('/dashboard/golden-goose')} 
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white transition-all"
              >
                <Award className="h-4 w-4 mr-2" />
                Find Golden Goose
              </Button>
            </CardContent>
          </Card>

          {/* Referral Verified Card */}
          <Card className="hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] border-green-100 hover:border-green-300">
            <CardHeader className="pb-2">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle className="text-green-900">Referral Verified</CardTitle>
              <CardDescription>Track and manage referrals</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4 min-h-[60px]">
                Create, verify, and track the progress of your referrals through the entire process.
              </p>
              <Button 
                onClick={() => navigate('/dashboard/create-referral')} 
                className="w-full bg-green-600 hover:bg-green-700 text-white transition-all"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Start Referral
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity Section */}
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4 text-gray-700">
            <History className="h-5 w-5 text-gray-500" />
            <h4 className="font-medium">Your latest actions</h4>
          </div>
          <div className="text-center py-8 text-gray-500">
            <p>Activity tracking is coming soon!</p>
            <p className="text-sm mt-2">We'll display your recent analyses and referrals here.</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Referral Verified Networking Tools. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
